"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
interface MenuItem {
  id: string;
  label: string;
  href?: string;
  children?: MenuItem[];
}
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex items-center gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-20 bg-muted animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent text-foreground hover:text-secondary transition-colors font-medium text-base">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[220px] gap-2 p-4 bg-popover rounded-md shadow-lg border border-border">
                    {item.children.map((child, j) => <li key={child.id} data-index={j}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground text-sm font-medium">
                            {child.label}
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-base font-medium transition-colors hover:text-secondary hover:bg-transparent focus:bg-transparent focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-transparent/50 data-[state=open]:bg-transparent/50 relative after:absolute after:bottom-1 after:left-4 after:right-4 after:h-[2px] after:bg-secondary after:scale-x-0 after:origin-center after:transition-transform hover:after:scale-x-100">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const toggleExpand = (id: string) => {
    setExpandedItems(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-40 bg-background/95 backdrop-blur-sm lg:hidden pt-24 px-6 overflow-y-auto">
      <nav className="flex flex-col space-y-6">
        {loading ? <div className="space-y-4">
            {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-8 w-full bg-muted animate-pulse rounded" />)}
          </div> : menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-border pb-4 last:border-0">
              {item.children && item.children.length > 0 ? <div>
                  <Button variant="ghost" onClick={() => toggleExpand(item.id)} className="w-full justify-between text-lg font-medium text-foreground hover:text-secondary hover:bg-transparent p-0 h-auto">
                    {item.label}
                    <ChevronDown className={cn("h-5 w-5 transition-transform duration-200", expandedItems.includes(item.id) ? "rotate-180" : "")} />
                  </Button>
                  {expandedItems.includes(item.id) && <div className="mt-4 ml-4 flex flex-col space-y-3 border-l-2 border-border pl-4">
                      {item.children.map((child, j) => <Link key={child.id} data-index={j} to={child.href || "#"} className="text-base text-muted-foreground hover:text-secondary transition-colors" onClick={onClose}>
                          {child.label}
                        </Link>)}
                    </div>}
                </div> : <Link to={item.href || "#"} className="block text-lg font-medium text-foreground hover:text-secondary transition-colors" onClick={onClose}>
                  {item.label}
                </Link>}
            </div>)}
        <div className="pt-4">
          <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 border-2 border-transparent hover:border-[color:var(--color-ring)] transition-all h-12 text-lg">
            <Link to="https://edustrategygroup.com/schedule-your-strategy-session/">Start a Conversation</Link>
          </Button>
        </div>
      </nav>
    </div>;
}
function HeaderContent() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <header className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-sm py-2" : "bg-background py-4")}>
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-center gap-8 md:gap-12">
          <Link to="/" className="flex-shrink-0">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1443870/img/edustrategy_brand_logo.png" alt="EduStrategy Group Logo" className="h-7" />
          </Link>

          {}
          <div className="hidden lg:block">
            <DesktopMenu />
          </div>

          {}
          <div className="hidden lg:block flex-shrink-0">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 border-2 border-transparent hover:border-[color:var(--color-ring)] transition-all font-bold uppercase tracking-wide text-xs px-6">
              <Link to="https://edustrategygroup.com/schedule-your-strategy-session/">Start a Conversation</Link>
            </Button>
          </div>

          {}
          <div className="lg:hidden">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:text-secondary hover:bg-transparent" aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </header>

      {}
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </>;
}
export default function MainNavigation() {
  return <MenuProvider menu_id="101" className="w-full">
      <HeaderContent />
    </MenuProvider>;
}